export function mergeStyles(...objects: Array<object>) {
  return objects.reduce((final, object) => {
    Object.entries(object).forEach(([key, value]) => {
      final[key] = { ...(final[key] || {}), ...value };
    });
    return final;
  }, {});
}

export function deepMergeStyles(...objects: object[]) {
  const isObject = (obj: object) => obj && typeof obj === 'object';

  function deepMergeInner(target: object, source: object) {
    Object.keys(source).forEach((key: string) => {
      const targetValue = target[key];
      const sourceValue = source[key];

      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        target[key] = targetValue.concat(sourceValue);
      } else if (isObject(targetValue) && isObject(sourceValue)) {
        target[key] = deepMergeInner(Object.assign({}, targetValue), sourceValue);
      } else {
        target[key] = sourceValue;
      }
    });

    return target;
  }

  if (objects.length < 2) {
    throw new Error('DeepMerge: this export function expects at least 2 objects to be provided');
  }

  if (objects.some((object) => !isObject(object))) {
    throw new Error('DeepMerge: all values should be of type "object"');
  }

  const target = objects.shift();
  let source: object;

  while ((source = objects.shift())) {
    deepMergeInner(target, source);
  }

  return target;
}

export const mediaQuery = (breakPoint: number): string =>
  `@media screen and (max-width: ${breakPoint}px)`;

export const canHover = (isHover = true): string => `@media (hover: ${isHover ? 'hover' : 'none'})`;

export const isLeftToRight = (isLeft = true): string => `[dir=${isLeft ? 'ltr' : 'rtl'}] & `;

export const ignoreRTL = (): string => '[dir] & ';

export const placeHolders = (color: string): Record<string, Record<string, string>> => ({
  '&::-webkit-input-placeholder': {
    color,
  },
  '&:-ms-input-placeholder': {
    color,
  },
  '&::placeholder': {
    color,
  },
});

export const placeHolderShown = (color?: string): Record<string, Record<string, string>> => ({
  '&:placeholder-shown': {
    color,
  },
});

export const supports = (prop: string, value: string): string => `@supports (${prop}: ${value})`;

export const supportsNot = (prop: string, value: string): string =>
  `@supports not (${prop}: ${value})`;

export const textOverflowHidden = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
};

export const selection = (
  backgroundColor = 'var(--x-accent-1-1)',
  color = 'var(--x-text-color-3)',
): Record<string, object> => {
  return {
    '@global': {
      '::-moz-selection': {
        backgroundColor,
        color,
      },
      '::selection': {
        backgroundColor,
        color,
      },
    },
  };
};

export const inputTypeNumberWithoutArrows = {
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
  },
  '-moz-appearance': 'textfield',
};

export const mozScrollbars = (
  thumbColor = 'var(--x-accent-1-1)',
  railColor = 'rgba(0,0,0,0.08)',
): Record<string, string> => ({
  scrollbarColor: `${thumbColor} ${railColor}`,
  scrollbarWidth: 'thin',
});

export enum ScrollbarCoverage {
  Local = '&',
  Global = '',
}

export interface IScrollbars {
  coverage?: ScrollbarCoverage;
  width?: number;
  height?: number;
  borderRadius?: number;
  trackBackgroundColor?: string;
  trackHoverBackgroundColor?: string;
  thumbBackgroundColor?: string;
  thumbHoverBackgroundColor?: string;
}

export const scrollbars = ({
  coverage = ScrollbarCoverage.Local,
  width = 4,
  height = 4,
  borderRadius = 0,
  trackBackgroundColor = 'rgba(0,0,0,0.08)',
  trackHoverBackgroundColor = '',
  thumbBackgroundColor = 'var(--x-accent-1-1)',
  thumbHoverBackgroundColor = 'var(--x-accent-1-action)',
}: IScrollbars = {}): Record<string, object> => {
  const styles = {
    [`${coverage}::-webkit-scrollbar`]: {
      width,
      height,
    },
    [`${coverage}::-webkit-scrollbar-track`]: {
      backgroundColor: trackBackgroundColor,

      [canHover()]: {
        '&:hover': {
          backgroundColor: trackHoverBackgroundColor,
        },
      },
    },
    [`${coverage}::-webkit-scrollbar-thumb`]: {
      borderRadius,
      backgroundColor: thumbBackgroundColor,

      [canHover()]: {
        '&:hover': {
          backgroundColor: thumbHoverBackgroundColor,
        },
      },
    },
  };

  return styles;
};

export const hideScrollbars = {
  scrollbarWidth: 'none',
  '-ms-overflow-style': 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
};

export const removeAutocompleteBackground = (color: string): Record<string, object> => ({
  // eslint-disable-next-line max-len
  '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus textarea:-webkit-autofill, &:-webkit-autofill:hover textarea:-webkit-autofill:focus, &:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus':
    {
      boxShadow: `0 0 0 1000px ${color} inset !important`,
    },
});

export function isLightMode() {
  return '@media (prefers-color-scheme: light)';
}

export function isDarkMode() {
  return '@media (prefers-color-scheme: dark)';
}

export function isLandscape() {
  return '@media screen and (orientation: landscape)';
}

export function isPortrait() {
  return '@media screen and (orientation: portrait)';
}

export function isOS() {
  return '@supports (-webkit-touch-callout: none)';
}
